import { useEffect, useState } from 'react';
import { ArrowLeft, Layers, Plus, Trash2 } from 'lucide-react';
import { api } from '../lib/api.js';
import { showAlert, showConfirm } from '../lib/tg.js';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemGroupContent,
  ItemGroupEmpty,
  ItemGroupHeader,
  ItemHeader,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item';
import { EmptyState, ErrorBanner } from '@/components/feedback';
import { cn } from '@/lib/utils';

interface SkillFile {
  content: string;
  is_executable: boolean;
}

interface Skill {
  id: string;
  name: string;
  description: string;
  files: Record<string, SkillFile>;
  updatedAt?: string | null;
}

const STARTER_SKILL_MD = `---
name: my_skill
description: One-line description shown in the agent's skill picker.
---

# my_skill

Body of the skill — instructions the agent should follow when this skill is
triggered. Reference companion files like \`reference.md\` or scripts under
\`scripts/\` as needed.
`;

export function SkillsView() {
  const [items, setItems] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const reload = async () => {
    try {
      setItems(await api.get<Skill[]>('/api/skills'));
    } catch (e) {
      setError((e as { message: string }).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  if (openId) {
    const skill = items.find((s) => s.id === openId);
    if (!skill) {
      setOpenId(null);
      return null;
    }
    return (
      <SkillEditor
        skill={skill}
        onBack={() => {
          setOpenId(null);
          void reload();
        }}
      />
    );
  }

  return (
    <ItemGroup>
      {error && <ErrorBanner>{error}</ErrorBanner>}
      <ItemGroupHeader
        title="Skills"
        description="Multi-file Claude Code skills. Tap a skill to edit its files."
        action={
          <Button size="sm" onClick={() => setShowCreate(true)}>
            <Plus />
            New
          </Button>
        }
      />

      <ItemGroupContent>
        {loading && items.length === 0 && <ItemGroupEmpty>loading…</ItemGroupEmpty>}
        {!loading && items.length === 0 && <ItemGroupEmpty>no skills yet</ItemGroupEmpty>}

        {items.map((s) => {
          const fileCount = Object.keys(s.files ?? {}).length;
          return (
            <Item key={s.id} interactive onClick={() => setOpenId(s.id)}>
              <ItemMedia>
                <Layers />
              </ItemMedia>
              <ItemContent>
                <ItemHeader>
                  <ItemTitle>{s.name}</ItemTitle>
                  <Badge variant="secondary" className="text-[10px]">
                    {fileCount} file{fileCount === 1 ? '' : 's'}
                  </Badge>
                </ItemHeader>
                {s.description && <ItemDescription>{s.description}</ItemDescription>}
              </ItemContent>
              <ItemActions>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={async () => {
                    if (!(await showConfirm(`Delete skill "${s.name}"?`))) return;
                    try {
                      await api.del(`/api/skills/${s.id}`);
                      void reload();
                    } catch (e) {
                      void showAlert((e as { message: string }).message);
                    }
                  }}
                >
                  <Trash2 />
                </Button>
              </ItemActions>
            </Item>
          );
        })}
      </ItemGroupContent>

      <CreateSkillDialog
        open={showCreate}
        onOpenChange={setShowCreate}
        onCreated={(id) => {
          setShowCreate(false);
          setOpenId(id);
          void reload();
        }}
      />
    </ItemGroup>
  );
}

function CreateSkillDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (id: string) => void;
}) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName('');
    setErr(null);
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New skill</DialogTitle>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="sk-name">Name</Label>
          <Input
            id="sk-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="my_skill"
          />
        </div>
        {err && <ErrorBanner>{err}</ErrorBanner>}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={busy || !name.trim()}
            onClick={async () => {
              setBusy(true);
              setErr(null);
              try {
                const r = await api.post<Skill>('/api/skills', {
                  name: name.trim(),
                  files: { 'SKILL.md': { content: STARTER_SKILL_MD, is_executable: false } },
                });
                onCreated(r.id);
              } catch (e) {
                setErr((e as { message: string }).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SkillEditor({ skill, onBack }: { skill: Skill; onBack: () => void }) {
  const [name, setName] = useState(skill.name);
  const [files, setFiles] = useState<Record<string, SkillFile>>(skill.files ?? {});
  const paths = Object.keys(files).sort();
  const [activePath, setActivePath] = useState<string>(paths[0] ?? 'SKILL.md');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [adding, setAdding] = useState(false);

  const save = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      await api.patch(`/api/skills/${skill.id}`, { name, files });
      setDirty(false);
    } catch (e) {
      setErr((e as { message: string }).message);
    } finally {
      setBusy(false);
    }
  };

  const updateFile = (path: string, patch: Partial<SkillFile>): void => {
    setFiles((prev) => ({ ...prev, [path]: { ...prev[path]!, ...patch } }));
    setDirty(true);
  };

  const renamePath = (oldPath: string, newPath: string): void => {
    if (oldPath === newPath || !newPath.trim()) return;
    if (files[newPath]) {
      setErr(`File "${newPath}" already exists`);
      return;
    }
    setFiles((prev) => {
      const next: Record<string, SkillFile> = {};
      for (const [k, v] of Object.entries(prev)) {
        next[k === oldPath ? newPath : k] = v;
      }
      return next;
    });
    setActivePath(newPath);
    setDirty(true);
  };

  const addFile = (path: string): void => {
    const trimmed = path.trim();
    if (!trimmed) return;
    if (files[trimmed]) {
      setErr(`File "${trimmed}" already exists`);
      return;
    }
    const isExec = trimmed.startsWith('scripts/');
    setFiles((prev) => ({ ...prev, [trimmed]: { content: '', is_executable: isExec } }));
    setActivePath(trimmed);
    setDirty(true);
    setAdding(false);
  };

  const deleteFile = async (path: string): Promise<void> => {
    if (!(await showConfirm(`Delete file "${path}"?`))) return;
    setFiles((prev) => {
      const next = { ...prev };
      delete next[path];
      return next;
    });
    if (activePath === path) {
      const remaining = Object.keys(files).filter((p) => p !== path);
      setActivePath(remaining[0] ?? '');
    }
    setDirty(true);
  };

  const active = activePath ? files[activePath] : undefined;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft />
          Skills
        </Button>
        <Input
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setDirty(true);
          }}
          className="max-w-[260px]"
        />
        <div className="flex-1" />
        <Button disabled={busy || !dirty} onClick={save}>
          {dirty ? 'Save' : 'Saved'}
        </Button>
      </div>

      {err && <ErrorBanner>{err}</ErrorBanner>}

      <div className="grid gap-3 lg:grid-cols-[260px_1fr] lg:items-start">
        <Card className="lg:sticky lg:top-4 lg:self-start">
          <CardHeader className="p-4 pb-2">
            <span className="font-semibold">Files</span>
          </CardHeader>
          <CardContent className="space-y-1 p-4 pt-0">
            {paths.length === 0 && <EmptyState>no files</EmptyState>}
            {paths.map((p) => (
              <div className="flex items-center gap-2" key={p}>
                <Button
                  variant={p === activePath ? 'default' : 'outline'}
                  size="sm"
                  className={cn('flex-1 justify-between font-mono text-xs')}
                  onClick={() => setActivePath(p)}
                >
                  <span className="truncate">{p}</span>
                  {files[p]?.is_executable && (
                    <span className="text-[10px] uppercase tracking-wide opacity-70">exec</span>
                  )}
                </Button>
                <Button variant="destructive" size="icon" onClick={() => void deleteFile(p)}>
                  <Trash2 />
                </Button>
              </div>
            ))}
            {!adding && (
              <Button variant="outline" size="sm" className="mt-2 w-full" onClick={() => setAdding(true)}>
                <Plus />
                Add file
              </Button>
            )}
            {adding && (
              <AddFileForm onCancel={() => setAdding(false)} onAdd={addFile} />
            )}
          </CardContent>
        </Card>

        {active && activePath && (
          <Card className="min-w-0">
            <CardHeader className="p-4 pb-2">
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  value={activePath}
                  onChange={(e) => renamePath(activePath, e.target.value)}
                  className="max-w-[320px] flex-1 font-mono text-xs"
                />
                <Label className="flex cursor-pointer items-center gap-2 text-xs font-normal text-muted-foreground">
                  <Checkbox
                    checked={active.is_executable}
                    onCheckedChange={(v) => updateFile(activePath, { is_executable: v === true })}
                  />
                  executable
                </Label>
              </div>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <Textarea
                value={active.content}
                onChange={(e) => updateFile(activePath, { content: e.target.value })}
                className="min-h-[60vh] font-mono text-xs"
              />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function AddFileForm({
  onCancel,
  onAdd,
}: {
  onCancel: () => void;
  onAdd: (path: string) => void;
}) {
  const [path, setPath] = useState('');
  return (
    <div className="mt-2 flex items-center gap-2">
      <Input
        value={path}
        onChange={(e) => setPath(e.target.value)}
        placeholder="reference.md or scripts/run.py"
        className="font-mono text-xs"
        autoFocus
      />
      <Button variant="outline" size="sm" onClick={onCancel}>
        Cancel
      </Button>
      <Button size="sm" disabled={!path.trim()} onClick={() => onAdd(path)}>
        Add
      </Button>
    </div>
  );
}
